import { Box, Button, createStyles, makeStyles, Typography } from '@material-ui/core';
import cn from 'classnames';
import moment from 'moment';
import * as React from 'react';
import { Link } from 'react-router-dom';
import AlertBase from '../../../components/Alerts/AlertBase';
import Btn from '../../../components/Btn/Btn';
import IconDialog from '../../../components/IconDialog/IconDialog';
import useDictionary from '../../../dictionaries/context/useDictionary';
import MarkdownRender from '../../../dictionaries/MarkdownRender';
import RenderText from '../../../dictionaries/RenderText';
import { NegotiationOption, OutDebt, useDeleteExpiredNegotiationMutation, useUpdateMaxPaymentDateExpiredNegotiationMutation } from '../../../generated/graphql';
import { loadingMessage } from '../../../utils/loadingMessage';
import { useGetNegotiationExpiredActionInfo } from '../../../utils/useGetNegotiationExpiredActionInfo';
import { TranquiTheme } from '../../App/theme';
import DebtDetailsNegotiationSummaryContent from '../DebtDetailsNegotiationSummaryContent';

interface DebtDetailsNegotiationConfirmationExpirationActionProps {
  outDebt: OutDebt;
  negOptions: NegotiationOption[];
  outDebts: OutDebt[];
  onReset: () => void;
}

export const DebtDetailsNegotiationConfirmationExpirationAction:
React.FC<DebtDetailsNegotiationConfirmationExpirationActionProps> = (props) => {
  const { outDebt, negOptions, outDebts, onReset } = props;
  const [ deleteExpired ] = useDeleteExpiredNegotiationMutation();
  const [ updateMaxPaymentDate ] = useUpdateMaxPaymentDateExpiredNegotiationMutation();
  const [ deleteExpiredLoading, setDeleteExpiredLoading ] = React.useState(false);

  const { negotiation } = outDebt;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();

  const {
    sameAvailable, otherAvailable, noneAvailable,
  } = useGetNegotiationExpiredActionInfo({ outDebt, negOptions });
  const { gT } = useDictionary();

  if (!negotiation) return null;

  const { settings = {} } = negOptions
    .find(({ type }) => type === negotiation.type) || {};
  const maxPaymentDate = moment(settings?.maxPaymentDate).startOf('day').toDate();
  const content = gT('negotiation_newPaymentDay_confirmation_EXPIRED', { maxPaymentDate });

  const updateNegotiationMaxPaymentDate = async () => {
    setDeleteExpiredLoading(true);
    await updateMaxPaymentDate({
      variables: { negotiationId: negotiation!.id, maxPaymentDate },
      refetchQueries: [ 'GetOutDebts', 'GetOutDebtNegotiationOptions' ],
      awaitRefetchQueries: true,
    });
    onReset();
    setDeleteExpiredLoading(false);
  };

  const handleDeleteExpired = async () => {
    setDeleteExpiredLoading(true);
    await deleteExpired({
      variables: {
        negotiationId: negotiation.id,
      },
      refetchQueries: [ 'GetOutDebts', 'GetOutDebtNegotiationOptions' ],
      awaitRefetchQueries: true,
    });
    onReset();
    setDeleteExpiredLoading(false);
  };

  return (
    <>
      {otherAvailable && (
        <Btn onClick={handleDeleteExpired}>
          <RenderText textKey="go_to_negotiation_options_btn_label" />
        </Btn>
      )}

      {sameAvailable && (
      <React.Fragment key="alert">
        <AlertBase
          type="alert"
          dataTest="cy-makePayment-alert"
          title="IMPORTANTE"
          className={cn(classes.alert)}
        >
          <MarkdownRender text={content} />
        </AlertBase>
        <Btn
          type="submit"
          fullWidth
          onClick={updateNegotiationMaxPaymentDate}
        >
          <RenderText textKey="confirm_new_max_payment_day_negotiation_btn_label" />
        </Btn>
      </React.Fragment>
      )}

      {noneAvailable && negOptions[0] && (
        <Box textAlign="center">
          <DebtDetailsNegotiationSummaryContent
            outDebt={outDebt}
            negOption={negOptions[0]}
          />
        </Box>
      )}

      {(outDebts.length > 1) && (
      <Button
        className={classes.allDebtsBtn}
        component={Link}
        to="/portal/deudas"
        data-test="cy-moreDebts-btn"
        variant="outlined"
        color="secondary"
      >
        Ver todas mis deudas
      </Button>
      )}

      {deleteExpiredLoading && (
        <IconDialog icon="loading" rotating open>
          <Typography variant="h3" align="center">{loadingMessage}</Typography>
        </IconDialog>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  allDebtsBtn: {
    color: theme.palette.lavender.main,
    textDecoration: 'underline',
    textAlign: 'center',
    fontWeight: 400,
    marginTop: theme.spacing(2),
  },
  alert: {
    whiteSpace: 'pre-line',
    marginBottom: theme.spacing(2),
  },
}));

DebtDetailsNegotiationConfirmationExpirationAction.displayName = 'DebtDetailsNegotiationConfirmationExpirationAction';
